import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import Root from "./routes/Root";
import AppRoot from "./routes/AppRoot";
import RootErrorBoundary from "./components/RootErrorBoundary";

// Lazy load pages for code splitting
const OnboardingPage = lazy(
  () => import("./pages/onboarding/OnboardingPage")
);

const NotFound = lazy(() => import("./pages/NotFound"));

const LegalHub = lazy(() => import("./pages/legal/LegalHub"));
const TermsAndConditions = lazy(() => import("./pages/legal/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const ContentRemoval = lazy(() => import("./pages/legal/ContentRemoval"));
const DmcaPolicy = lazy(() => import("./pages/legal/DmcaPolicy"));
const ComplaintPolicy = lazy(() => import("./pages/legal/ComplaintPolicy"));
const UnderagePolicy = lazy(() => import("./pages/legal/UnderagePolicy"));
const BlockedContent = lazy(() => import("./pages/legal/BlockedContent"));
const Usc2257Exemption = lazy(() => import("./pages/legal/Usc2257Exemption"));
const ComplianceStatement = lazy(
  () => import("./pages/legal/ComplianceStatement")
);
const ChargebackPolicy = lazy(() => import("./pages/legal/ChargebackPolicy"));
const AntiSlavery = lazy(() => import("./pages/legal/AntiSlavery"));
const AiDisclosure = lazy(() => import("./pages/legal/AiDisclosure"));

// App pages (new scaffold)
const ExplorePage = lazy(() => import("./pages/ExplorePage"));
const AuthMethodPicker = lazy(() => import("./pages/auth/AuthMethodPicker"));
const EmailLinkSignIn = lazy(() => import("./pages/auth/EmailLinkSignIn"));
const FinishSignIn = lazy(() => import("./pages/auth/FinishSignIn"));
const PasswordAuthPage = lazy(() => import("./pages/auth/PasswordAuthPage"));
const SetPasswordPage = lazy(() => import("./pages/auth/SetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));
const TestingAuthPage = lazy(() => import("./pages/auth/TestingAuthPage"));
const ChatsPage = lazy(() => import("./pages/ChatsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CharacterChatLandingPage = lazy(
  () => import("./pages/CharacterChatLandingPage")
);
const MediaViewerPage = lazy(() => import("./pages/MediaViewerPage"));
const PaywallSubscribePage = lazy(() => import("./pages/paywall/PaywallSubscribePage"));
const PaywallTokensPage = lazy(() => import("./pages/paywall/PaywallTokensPage"));
const PaywallNetValveCheckoutPage = lazy(
  () => import("./pages/paywall/PaywallNetValveCheckoutPage")
);
const PaymentSuccessScreen = lazy(() => import("./pages/paywall/PaymentSuccessScreen"));
const PaymentFailureScreen = lazy(() => import("./pages/paywall/PaymentFailureScreen"));
const OnboardingEntryLandingPage = lazy(
  () => import("./pages/onboarding/OnboardingEntryLandingPage")
);
const OnboardingSuccessScreen = lazy(() => import("./pages/onboarding/OnboardingSuccessScreen"));
const OnboardingFailureScreen = lazy(() => import("./pages/onboarding/OnboardingFailureScreen"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage"));
const ReportPage = lazy(() => import("./pages/ReportPage"));

// Admin pages (lazy)
const AdminRoot = lazy(() => import("./admin/AdminRoot"));
const AdminHome = lazy(() => import("./admin/pages/AdminHome"));
const AdminCharactersPage = lazy(() => import("./admin/pages/CharactersPage"));
const AdminCharacterDetailPage = lazy(() => import("./admin/pages/CharacterDetailPage"));
const AdminCharacterTranslationsPage = lazy(
  () => import("./admin/pages/CharacterTranslationsPage")
);
const AdminNewestMediaPage = lazy(() => import("./admin/pages/RecentMediaPage"));
const AdminUsersPage = lazy(() => import("./admin/pages/UsersPage"));
const AdminUserDetailPage = lazy(() => import("./admin/pages/UserDetailPage"));
const AdminEmailLogsPage = lazy(() => import("./admin/pages/EmailLogsPage"));
const AdminCompliancePage = lazy(() => import("./admin/pages/CompliancePage"));
const AdminInvoicesPage = lazy(() => import("./admin/pages/InvoicesPage"));

export const router = createBrowserRouter([
  // Main app routes with TopBelt
  {
    path: "/",
    element: <AppRoot />,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        element: <ExplorePage />,
      },
      {
        path: "explore",
        element: <ExplorePage />,
      },
      {
        path: "auth/signin",
        element: <AuthMethodPicker />,
      },
      {
        path: "auth/email-signin",
        element: <EmailLinkSignIn />,
      },
      {
        path: "auth/login",
        element: <AuthMethodPicker />,
      },
      {
        path: "auth/signup",
        element: <AuthMethodPicker />,
      },
      {
        path: "auth/finish-signin",
        element: <FinishSignIn />,
      },
      {
        path: "auth/password",
        element: <PasswordAuthPage />,
      },
      {
        path: "auth/set-password",
        element: <SetPasswordPage />,
      },
      {
        path: "auth/verify-email",
        element: <VerifyEmailPage />,
      },
      {
        path: "chats",
        element: <ChatsPage />,
      },
      {
        path: "chats/:id",
        element: <ChatsPage />,
      },
      {
        path: "chat/:characterId",
        element: <CharacterChatLandingPage />,
      },
      {
        path: "profile/:id",
        element: <ProfilePage />,
      },
      {
        path: "viewer/:girlId",
        element: <MediaViewerPage />,
      },
      {
        path: "paywall/subscribe",
        element: <PaywallSubscribePage />,
      },
      {
        path: "paywall/tokens",
        element: <PaywallTokensPage />,
      },
      {
        path: "paywall/checkout-netvalve",
        element: <PaywallNetValveCheckoutPage />,
      },
      {
        path: "paywall/success",
        element: <PaymentSuccessScreen />,
      },
      {
        path: "paywall/failure",
        element: <PaymentFailureScreen />,
      },
      {
        path: "settings",
        element: <SettingsPage />,
      },
      {
        path: "contact",
        element: <ContactUsPage />,
      },
      {
        path: "report",
        element: <ReportPage />,
      },
      {
        path: "onboarding",
        element: <Navigate to="/onboarding/landing" replace />,
      },
      {
        path: "onboarding/landing",
        element: <OnboardingEntryLandingPage />,
      },
      {
        path: "onboarding/create",
        element: <OnboardingPage />,
      },
      {
        path: "onboarding/create/:stepId",
        element: <OnboardingPage />,
      },
      {
        path: "onboarding/kink-quiz",
        element: <OnboardingPage useKinkQuizFlow />,
      },
      {
        path: "onboarding/kink-quiz/:stepId",
        element: <OnboardingPage useKinkQuizFlow />,
      },
      {
        path: "onboarding/success",
        element: <OnboardingSuccessScreen />,
      },
      {
        path: "onboarding/failure",
        element: <OnboardingFailureScreen />,
      },
    ],
  },
  {
    path: "/testing-auth",
    element: <TestingAuthPage />,
  },
  // Legal pages - using the original Root layout
  {
    path: "/legal",
    element: <Root />,
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <LegalHub /> },
      { path: "terms", element: <TermsAndConditions /> },
      { path: "privacy", element: <PrivacyPolicy /> },
      { path: "content-removal", element: <ContentRemoval /> },
      { path: "dmca", element: <DmcaPolicy /> },
      { path: "complaint", element: <ComplaintPolicy /> },
      { path: "underage", element: <UnderagePolicy /> },
      { path: "blocked-content", element: <BlockedContent /> },
      { path: "2257", element: <Usc2257Exemption /> },
      { path: "compliance", element: <ComplianceStatement /> },
      { path: "chargeback", element: <ChargebackPolicy /> },
      { path: "anti-slavery", element: <AntiSlavery /> },
      { path: "ai-disclosure", element: <AiDisclosure /> },
    ],
  },
  {
    path: "/admin",
    element: <AdminRoot />,
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <AdminHome /> },
      { path: "characters", element: <AdminCharactersPage /> },
      { path: "characters/:id", element: <AdminCharacterDetailPage /> },
      { path: "translations", element: <AdminCharacterTranslationsPage /> },
      { path: "media", element: <AdminNewestMediaPage /> },
      { path: "users", element: <AdminUsersPage /> },
      { path: "users/:id", element: <AdminUserDetailPage /> },
      { path: "email-logs", element: <AdminEmailLogsPage /> },
      { path: "compliance", element: <AdminCompliancePage /> },
      { path: "invoices", element: <AdminInvoicesPage /> },
    ],
  },
  // Catch all
  {
    path: "*",
    element: <NotFound />,
  },
]);
